import { Dispatch, Ref, SetStateAction, useEffect, useState } from "react";
import packageJson from "../../../package.json";
import "./Menu.css";
import DarkModeToggle from "./DarkModeToggle/DarkModeToggle";
import { useUtilImages } from "../../Services/Hooks/useUtilImages";
import { usePWAInstall } from "../../Services/Hooks/usePWAInstall";
import AboutUs from "../AboutUs/AboutUs";
import { useDarkMode } from "../../Services/Context/DarkModeContext";
import { useLanguage } from "../../Services/Context/LanguageContext";
import { Language, languageNames } from "../../i18n/translations";

const Menu = ({
  setIsReversedCardsEnabled,
  isReversedCardsEnabled,
  aiEnabled,
  setAiEnabled,
  menuClickArea,
  isMenuClosing,
  setIsMenuClosing,
}: {
  setIsReversedCardsEnabled: Dispatch<SetStateAction<boolean>>;
  isReversedCardsEnabled: boolean;
  aiEnabled: boolean;
  setAiEnabled: Dispatch<SetStateAction<boolean>>;
  menuClickArea: Ref<HTMLDivElement>;
  isMenuClosing: boolean;
  setIsMenuClosing: Dispatch<SetStateAction<boolean>>;
}) => {
  const { dark } = useDarkMode();
  const { down, book } = useUtilImages();
  const { language, setLanguage, translate } = useLanguage();
  const { canInstall, triggerInstall } = usePWAInstall();
  const [showMenu, setShowMenu] = useState(false);
  const [isAboutUsOpen, setIsAboutUsOpen] = useState(false);

  useEffect(() => {
    if (isMenuClosing) {
      setTimeout(() => {
        setShowMenu(false);
        setIsMenuClosing(false);
      }, 200);
    }
  }, [isMenuClosing, setIsMenuClosing]);

  return (
    <>
      <div className="Menu" ref={menuClickArea}>
        <button
          onClick={() => {
            setIsMenuClosing(false);
            setShowMenu(true);
          }}
        >
          <img
            className={dark ? "Menu-book-dark" : "Menu-book"}
            src={book}
            alt="book"
            onMouseEnter={() => setShowMenu(true)}
          />
        </button>
        {showMenu && (
          <nav
            className={`${dark ? "Menu-nav-dark" : "Menu-nav"} animate__animated animate__fadeInUpBig ${isMenuClosing ? "animate__fadeOut" : ""}`}
            onMouseLeave={() => setIsMenuClosing(true)}
          >
            <div className="Menu-sigil">✦ ✦ ✦</div>
            <ul>
              <li>
                <div className="Menu-toggle-row">
                  <span className="Menu-toggle-label">
                    {translate("reversedCards")}
                  </span>
                  <label className="Menu-switch">
                    <input
                      type="checkbox"
                      checked={isReversedCardsEnabled}
                      onChange={() =>
                        setIsReversedCardsEnabled((prev) => !prev)
                      }
                    />
                    <span className="Menu-slider" />
                  </label>
                </div>
              </li>
              <li>
                <div className="Menu-toggle-row">
                  <span className="Menu-toggle-label">
                    {translate("aiReading")}
                  </span>
                  <label className="Menu-switch">
                    <input
                      type="checkbox"
                      checked={aiEnabled}
                      onChange={() => setAiEnabled((prev) => !prev)}
                    />
                    <span className="Menu-slider" />
                  </label>
                </div>
              </li>
              <li>
                <div className="Menu-toggle-row">
                  <span className="Menu-toggle-label">
                    {translate("language")}
                  </span>
                  <select
                    className="Menu-language-select"
                    value={language}
                    onChange={(e) => setLanguage(e.target.value as Language)}
                  >
                    {(Object.keys(languageNames) as Language[]).map((lang) => (
                      <option key={lang} value={lang}>
                        {languageNames[lang]}
                      </option>
                    ))}
                  </select>
                </div>
              </li>
              <li>
                <div className="Menu-divider" />
              </li>
              <li>
                <DarkModeToggle />
              </li>
              <li>
                <div className="Menu-divider" />
              </li>
              {canInstall && (
                <li>
                  <button
                    onClick={triggerInstall}
                    className="Menu-install-button"
                  >
                    {translate("installApp")}
                  </button>
                </li>
              )}
              <li>
                <a
                  href="https://buymeacoffee.com/fatespin"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="Menu-bmc-link"
                >
                  ☕ {translate("buyMeACoffee")}
                </a>
              </li>
              <li>
                <button
                  onClick={() => {
                    setIsAboutUsOpen(true);
                    setIsMenuClosing(true);
                  }}
                  className="Menu-about-us-link"
                >
                  {translate("moreAboutUs")}
                </button>
              </li>
              <li>
                <span className="Menu-version">
                  v{packageJson.version} Beta
                </span>
              </li>
            </ul>
            <button onClick={() => setIsMenuClosing(true)}>
              <img className="Menu-down-button" src={down} alt="close menu" />
            </button>
          </nav>
        )}
      </div>
      {isAboutUsOpen && <AboutUs setIsAboutUsOpen={setIsAboutUsOpen} />}
    </>
  );
};

export default Menu;
